import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Command } from "cmdk";
import * as Popover from "@radix-ui/react-popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { smartTruncateMiddle } from "../../helpers/utils";
import { authenticatedFetch } from "../../api/authenticatedFetch";

// ─────────────────────────────────────────────────────────────────────────────
// Fetch helper with AbortController support
// ─────────────────────────────────────────────────────────────────────────────

async function fetchSearchTerms(request, signal) {
  const response = await authenticatedFetch(
    `/get-search-terms-v2?useTagKeys=true`,
    {
      method: "POST",
      body: request,
      signal,
    }
  );

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}${response.statusText ? `: ${response.statusText}` : ''}`;
    try {
      const errorData = await response.json();
      if (errorData && (errorData.message || errorData.error)) {
        errorMessage = errorData.message || errorData.error || errorMessage;
      }
    } catch (parseError) {
      // If JSON parsing fails, use the status-based message
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Debounce hook
// ─────────────────────────────────────────────────────────────────────────────

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const GlobalSearchBox = ({
  onSelectTerm,
  onAsk,
  placeholder = "Ask...",
  pageSize = 100,
  token,
  filters = [],
  excludeCategories = [],
  isEditMode = false,
  mode = "search",
  recentQuestions = [],
  widthClassName,
}) => {
  // ─── State ─────────────────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [hasSelectedItem, setHasSelectedItem] = useState(false); // Track if cmdk has a selected item
  const [isListHovered, setIsListHovered] = useState(false); // Track if mouse is over the list
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false); // Track if using keyboard navigation
  const pendingLoadingRef = useRef(false); // Track if we're waiting for items to render
  const loadingRef = useRef(false); // Track loading state to avoid stale closures

  // Ask mode is always "about" (about this view) for now
  const askMode = "about";
  
  // Item is effectively selected when (hovering OR keyboard navigating) AND an item is selected
  const isItemEffectivelySelected = (isListHovered || isKeyboardNavigating) && hasSelectedItem;

  // ─── Refs ──────────────────────────────────────────────────────────────────
  const abortControllerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const commandRef = useRef(null);
  const cmdInputRef = useRef(null);
  // Track if we're in the middle of opening (to ignore spurious close events)
  const isOpeningRef = useRef(false);

  // Debounced query for search
  const debouncedQuery = useDebounce(query, 150);

  // ─── Global keyboard shortcut (Cmd+K / Ctrl+K) ─────────────────────────────
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Mark that we're programmatically opening to ignore spurious close events
        isOpeningRef.current = true;
        
        // Open and trigger a fetch
        setOpen(true);
        setFetchTrigger((prev) => prev + 1);
        
        setTimeout(() => {
          inputRef.current?.focus();
          // Clear the flag after focus is established
          setTimeout(() => {
            isOpeningRef.current = false;
          }, 100);
        }, 50);
      }
    };

    // Use capture phase to intercept before kbar
    document.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
  }, []);

  // ─── Fetch data ────────────────────────────────────────────────────────────
  const fetchData = useCallback(
    async (searchQuery, category, currentOffset, append) => {
      // Abort any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Prevent double-fetch while loading
      if (loadingRef.current && append) return;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      loadingRef.current = true;
      setError(null);
      pendingLoadingRef.current = !append; // Set pending flag for non-append operations

      try {
        const request = {
          search_term: searchQuery,
          category: category || "",
          filters,
          exclude_categories: excludeCategories,
          offset: currentOffset,
          limit: pageSize,
        };

        const response = await fetchSearchTerms(request, controller.signal);
        const options = response.options || [];

        if (append) {
          setItems((prev) => [...prev, ...options]);
          // For append operations, clear loading immediately since we're just adding to existing items
          setLoading(false);
          loadingRef.current = false;
          pendingLoadingRef.current = false;
        } else {
          setItems(options);
          // Keep loading true - will be cleared by useEffect when items are rendered
        }
        setHasMore(response.hasMore || false);
        setOffset(currentOffset + options.length);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Failed to fetch results");
          setLoading(false);
          loadingRef.current = false;
          pendingLoadingRef.current = false;
        }
        // AbortError - request was cancelled, don't clear loading as new request is starting
      }
    },
    [filters, excludeCategories, pageSize]
  );

  // ─── Clear selection helper ──────────────────────────────────────────────────
  const clearSelection = useCallback(() => {
    // Blur the command input to clear cmdk's internal selection
    if (cmdInputRef.current) {
      cmdInputRef.current.blur();
    }
    // Remove selected state from all items
    if (listRef.current) {
      const selectedItems = listRef.current.querySelectorAll('[data-selected="true"]');
      selectedItems.forEach((item) => {
        item.removeAttribute('data-selected');
      });
    }
  }, []);

  // ─── Effect: Clear loading after items are rendered ─────────────────────────
  useEffect(() => {
    if (pendingLoadingRef.current && open) {
      let timeoutId = null;
      let observer = null;
      let rafId = null;
      
      const clearLoading = () => {
        if (pendingLoadingRef.current) {
          // Use requestAnimationFrame to ensure DOM has updated before clearing loading
          requestAnimationFrame(() => {
            if (pendingLoadingRef.current) {
              setLoading(false);
              loadingRef.current = false;
              pendingLoadingRef.current = false;
            }
          });
        }
      };
      
      const checkItemsRendered = () => {
        if (!pendingLoadingRef.current || !listRef.current) return false;
        
        // Check if we have the expected number of items in the DOM
        const renderedItems = listRef.current.querySelectorAll('[cmdk-item]');
        const expectedItemCount = items.length;
        
        // Also check for error state or "no results" message
        const hasError = listRef.current.querySelector('.text-danger');
        const hasNoResults = listRef.current.querySelector('.text-text-2.text-center');
        
        if (renderedItems.length >= expectedItemCount || 
            (expectedItemCount === 0 && (renderedItems.length === 0 || hasNoResults)) ||
            hasError) {
          return true;
        }
        return false;
      };
      
      // Use MutationObserver for immediate detection when items are added
      if (listRef.current) {
        observer = new MutationObserver(() => {
          if (checkItemsRendered()) {
            clearLoading();
            if (observer) observer.disconnect();
          }
        });
        
        observer.observe(listRef.current, {
          childList: true,
          subtree: true,
        });
        
        // Also do an immediate check in case items are already rendered
        rafId = requestAnimationFrame(() => {
          if (checkItemsRendered()) {
            clearLoading();
            if (observer) observer.disconnect();
          }
        });
      } else {
        // If listRef not available yet, poll briefly until it is
        let checkCount = 0;
        const maxChecks = 10; // ~160ms max wait for listRef
        
        const pollForListRef = () => {
          checkCount++;
          if (checkCount > maxChecks) {
            // Safety fallback - clear loading even if listRef never appears
            clearLoading();
            return;
          }
          
          if (listRef.current) {
            // Now set up MutationObserver
            observer = new MutationObserver(() => {
              if (checkItemsRendered()) {
                clearLoading();
                if (observer) observer.disconnect();
              }
            });
            
            observer.observe(listRef.current, {
              childList: true,
              subtree: true,
            });
            
            // Immediate check
            rafId = requestAnimationFrame(() => {
              if (checkItemsRendered()) {
                clearLoading();
                if (observer) observer.disconnect();
              }
            });
          } else {
            timeoutId = setTimeout(pollForListRef, 16);
          }
        };
        
        rafId = requestAnimationFrame(pollForListRef);
      }
      
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (observer) observer.disconnect();
        if (rafId) cancelAnimationFrame(rafId);
      };
    }
  }, [items, open]);

  // ─── Effect: Fetch on query/category change or Cmd+K trigger ───────────────
  useEffect(() => {
    if (mode === "ask") return; // ask mode never searches entities
    if (open && token) {
      setOffset(0);
      // Don't set loading here - let fetchData handle it to avoid race conditions
      // fetchData will set loading=true and pendingLoadingRef appropriately
      fetchData(debouncedQuery, activeCategory, 0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, activeCategory, open, fetchTrigger, token, mode]);

  // ─── Effect: Clear selection when query changes ────────────────────────────
  useEffect(() => {
    if (open) {
      clearSelection();
    }
  }, [query, open, clearSelection]);

  // ─── Effect: Clear selection when focus leaves search results ──────────────
  useEffect(() => {
    if (!open || !listRef.current) return;

    const handleFocusOut = (e) => {
      // Check if focus is moving outside the list container
      const currentTarget = e.currentTarget;
      const relatedTarget = e.relatedTarget;
      
      // Clear selection when focus leaves the list area (to ask button, input, or anywhere else)
      if (relatedTarget && !currentTarget.contains(relatedTarget)) {
        // Use a small delay to ensure the focus change has completed
        setTimeout(() => {
          clearSelection();
        }, 0);
      }
    };

    const listElement = listRef.current;
    listElement.addEventListener('focusout', handleFocusOut);
    
    return () => {
      listElement.removeEventListener('focusout', handleFocusOut);
    };
  }, [open, clearSelection]);

  // ─── Effect: Track cmdk selection state via MutationObserver ────────────────
  useEffect(() => {
    if (!open || !listRef.current) {
      setHasSelectedItem(false);
      return;
    }

    const checkForSelection = () => {
      const selectedItem = listRef.current?.querySelector('[data-selected="true"]');
      setHasSelectedItem(!!selectedItem);
    };

    // Initial check
    checkForSelection();

    // Observe changes to data-selected attribute
    const observer = new MutationObserver(checkForSelection);
    observer.observe(listRef.current, {
      attributes: true,
      attributeFilter: ['data-selected'],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [open, items]);

  // ─── Infinite scroll handler ───────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const threshold = 0.85;

    if (scrollTop + clientHeight >= scrollHeight * threshold) {
      fetchData(debouncedQuery, activeCategory, offset, true);
    }
  }, [loading, hasMore, debouncedQuery, activeCategory, offset, fetchData]);

  // ─── Derive categories from items (for All mode) ───────────────────────────
  const { categoryItems, termItems } = useMemo(() => {
    // Guard against null/undefined items
    const safeItems = items || [];
    
    if (activeCategory) {
      // In drilled mode, all items are term items (filter out any Category items)
      return {
        categoryItems: [],
        termItems: safeItems.filter((item) => item.category !== "Category"),
      };
    }

    // In All mode, separate category rows from term rows
    const cats = safeItems.filter((item) => item.category === "Category");
    const terms = safeItems.filter((item) => item.category !== "Category");
    return { categoryItems: cats, termItems: terms };
  }, [items, activeCategory]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleCategoryClick = (item) => {
    // Drill into category
    setActiveCategory(item.term);
    setOffset(0);
    setItems([]);
  };

  const handleTermClick = (item) => {
    onSelectTerm(item);
    // Close the search box after selection
    setOpen(false);
    setQuery("");
  };

  const handleBreadcrumbClick = () => {
    // Return to All mode
    setActiveCategory(null);
    setOffset(0);
    setItems([]);
  };

  // ─── Keyboard handling for Command items ────────────────────────────────────
  const handleCommandItemKeyDown = (e) => {
    // Tab cycles back to search input
    if (e.key === "Tab") {
      e.preventDefault();
      setIsKeyboardNavigating(false);
      clearSelection();
      isOpeningRef.current = true;
      inputRef.current?.focus();
      setTimeout(() => {
        isOpeningRef.current = false;
      }, 200);
    }
    // ArrowUp on first item returns to input
    if (e.key === "ArrowUp") {
      // Check if this is the first item
      const firstItem = listRef.current?.querySelector('[cmdk-item]');
      const currentItem = e.currentTarget;
      if (firstItem === currentItem || !listRef.current?.querySelector('[cmdk-item][data-selected="true"]')) {
        e.preventDefault();
        setIsKeyboardNavigating(false);
        clearSelection();
        isOpeningRef.current = true;
        inputRef.current?.focus();
        setTimeout(() => {
          isOpeningRef.current = false;
        }, 200);
      }
    }
  };

  // ─── Keyboard handling for input ───────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Tab" && !e.shiftKey && open) {
      e.preventDefault();
      // Focus the first search result item
      setTimeout(() => {
        if (listRef.current) {
          // Try to find the first Command.Item
          const firstItem = listRef.current.querySelector('[cmdk-item]');
          if (firstItem && firstItem instanceof HTMLElement) {
            firstItem.setAttribute('tabindex', '0');
            firstItem.focus();
          }
        }
      }, 0);
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
    // ArrowDown selects first item or moves to next item
    if (e.key === "ArrowDown" && open) {
      e.preventDefault();
      
      if (!isKeyboardNavigating || !hasSelectedItem) {
        // First press: select the first item directly via DOM
        setIsKeyboardNavigating(true);
        
        if (listRef.current) {
          // Clear all existing selections
          const selectedItems = listRef.current.querySelectorAll('[data-selected="true"]');
          selectedItems.forEach((item) => {
            if (item instanceof HTMLElement) {
              item.dataset.selected = 'false';
            }
          });
          
          // Select the first item
          const firstItem = listRef.current.querySelector('[cmdk-item]');
          if (firstItem instanceof HTMLElement) {
            firstItem.dataset.selected = 'true';
          }
        }
        
        // Focus cmdk input to enable subsequent keyboard navigation
        if (cmdInputRef.current) {
          cmdInputRef.current.focus();
        }
      } else {
        // Subsequent press: let cmdk handle navigation to next item
        if (cmdInputRef.current) {
          cmdInputRef.current.focus();
          cmdInputRef.current.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'ArrowDown',
            code: 'ArrowDown',
            bubbles: true,
            cancelable: true,
          }));
        }
      }
    }
    // ArrowUp when no item selected does nothing (stay in input)
    if (e.key === "ArrowUp" && open) {
      e.preventDefault();
      // If keyboard navigating and there's a selection, let cmdk handle it
      if (isKeyboardNavigating && hasSelectedItem && cmdInputRef.current) {
        cmdInputRef.current.focus();
        cmdInputRef.current.dispatchEvent(new KeyboardEvent('keydown', {
          key: 'ArrowUp',
          code: 'ArrowUp',
          bubbles: true,
          cancelable: true,
        }));
      }
    }
    // Enter: if item effectively selected (hovering + selected), select it; otherwise ask
    if (e.key === "Enter" && open) {
      e.preventDefault();
      if (isItemEffectivelySelected && cmdInputRef.current) {
        cmdInputRef.current.focus();
        // Trigger Enter on the cmdk input to select the item
        setTimeout(() => {
          cmdInputRef.current?.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            bubbles: true,
            cancelable: true,
          }));
        }, 0);
      } else if (query.trim()) {
        // No item selected, ask the query
        onAsk(query.trim(), askMode);
      }
    }
  };

  // ─── Handle input change ─────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    setQuery(e.target.value);
    // Clear selection and keyboard navigation when typing
    clearSelection();
    setIsKeyboardNavigating(false);
    if (!open) {
      setOpen(true);
    }
  };

  // ─── Handle input focus/click ──────────────────────────────────────────────
  const handleInputInteraction = () => {
    // Always set the flag when input gets focus to prevent popover from closing
    // This is especially important when returning focus from the ask button
    isOpeningRef.current = true;
    if (!open) {
      setOpen(true);
    }
    // Clear the flag after a short delay
    setTimeout(() => {
      isOpeningRef.current = false;
    }, 200);
  };

  // ─── Handle popover open change ────────────────────────────────────────────
  const handleOpenChange = (newOpen) => {
    // If we're in the middle of opening, ignore close events
    if (!newOpen && isOpeningRef.current) {
      return;
    }
    setOpen(newOpen);
  };

  // ─── Highlight matching text in search results ────────────────────────────────
  const highlightMatch = useCallback((text, searchQuery) => {
    if (!searchQuery || !text) return text;
    
    const query = searchQuery.trim().toLowerCase();
    if (!query) return text;
    
    const textLower = text.toLowerCase();
    const matchIndex = textLower.indexOf(query);
    
    if (matchIndex === -1) return text;
    
    const before = text.slice(0, matchIndex);
    const match = text.slice(matchIndex, matchIndex + query.length);
    const after = text.slice(matchIndex + query.length);
    
    return (
      <>
        {before}
        <span className="font-semibold text-text">{match}</span>
        {after}
      </>
    );
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  const defaultWidthCls = mode === "ask" ? "w-[clamp(320px,26vw,520px)]" : "w-[clamp(280px,22vw,420px)]";
  const widthCls = widthClassName || defaultWidthCls;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Anchor asChild>
        <div
          // Compact bordered launcher — white, 1px light neutral border (lighter than
          // the buttons), no pale-blue fill — so it reads as a secondary launcher, not
          // a primary search field. Expands on focus for typing.
          className={`flex items-center gap-2 ${widthCls} h-[36px] px-2.5 rounded-[10px] bg-white border border-solid border-border cursor-text transition-[width,background-color,border-color] hover:border-card-bd focus-within:bg-white focus-within:border-[#BFC8DA] focus-within:shadow-[0_0_0_3px_rgba(98,93,245,0.14)]`}
          style={{ minHeight: 0 }}
          onClick={(e) => {
            // Prevent popover from closing when clicking anywhere in the input container
            e.stopPropagation();
            handleInputInteraction();
          }}
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          onKeyDown={() => { /* Keyboard handled by input inside */ }}
        >
          {loading ? (
            <FontAwesomeIcon
              icon={icon({
                name: "spinner",
                family: "sharp",
                style: "solid",
              })}
              className="text-[#64748B] text-[14px] animate-spin"
              style={{ pointerEvents: "none" }}
            />
          ) : mode === "ask" ? (
            <FontAwesomeIcon
              icon={icon({
                name: "magnifying-glass",
                family: "sharp",
                style: "regular",
              })}
              className="text-[#64748B] text-[14px]"
              style={{ pointerEvents: "none" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={icon({
                name: "magnifying-glass",
                family: "sharp",
                style: "regular",
              })}
              className="text-[#64748B] text-[14px]"
              style={{ pointerEvents: "none" }}
            />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputInteraction}
            onClick={handleInputInteraction}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-transparent outline-none text-[#111827] placeholder:text-[#94A3B8]"
            style={{ fontSize: "14px", fontWeight: 500, lineHeight: "inherit" }}
          />
          {/* Action indicator: shows what Enter will do (not focusable, purely informational) */}
          {open && (query.trim() || isItemEffectivelySelected) && (
            <button
              type="button"
              tabIndex={-1}
              onClick={() => {
                if (isItemEffectivelySelected && cmdInputRef.current) {
                  cmdInputRef.current.focus();
                  setTimeout(() => {
                    cmdInputRef.current?.dispatchEvent(new KeyboardEvent('keydown', {
                      key: 'Enter',
                      code: 'Enter',
                      bubbles: true,
                      cancelable: true,
                    }));
                  }, 0);
                } else if (query.trim()) {
                  onAsk(query.trim(), askMode);
                }
              }}
              className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded border cursor-pointer transition-colors select-none ${
                mode === "ask" && !isEditMode && !isItemEffectivelySelected
                  ? "text-primary bg-primary-soft border-transparent hover:brightness-95"
                  : "text-text-2 bg-surface-2 border-border hover:bg-surface-3 hover:text-text"
              }`}
              style={{ userSelect: 'none' }}
            >
              <span>{isEditMode ? 'Add filter' : (isItemEffectivelySelected ? 'Select' : 'Ask')}</span>
              <span className="text-[10px] opacity-70">↵</span>
            </button>
          )}
          {mode !== "ask" && (
            <FontAwesomeIcon
              icon={icon({
                name: "angle-down",
                family: "sharp",
                style: "regular",
              })}
              className="text-[#64748B] text-[18px] ml-auto"
              style={{ pointerEvents: "none" }}
            />
          )}
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          className="w-[720px] max-w-[calc(100vw-24px)] rounded-xl border border-card-bd bg-card shadow-float z-[9999] outline-none rounded-[16px]"
          sideOffset={8}
          align="start"
          alignOffset={-100}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command ref={commandRef} shouldFilter={false} className="flex flex-col relative overflow-hidden rounded-[16px]">
            {/* Hidden input to sync with external input for cmdk keyboard navigation */}
            <Command.Input
              ref={cmdInputRef}
              value={query}
              onValueChange={(val) => {
                setQuery(val);
                // Return focus to visible input after cmdk processes the value
                inputRef.current?.focus();
              }}
              onKeyDown={(e) => {
                // Return focus to main input when typing
                if (e.key.length === 1 || e.key === "Backspace") {
                  setIsKeyboardNavigating(false);
                  inputRef.current?.focus();
                }
                // ArrowUp on first item (or no selection) returns to input
                if (e.key === "ArrowUp") {
                  const firstItem = listRef.current?.querySelector('[cmdk-item]');
                  const selectedItem = listRef.current?.querySelector('[cmdk-item][data-selected="true"]');
                  // If no item selected yet, or selected item is the first one
                  if (!selectedItem || selectedItem === firstItem) {
                    e.preventDefault();
                    setIsKeyboardNavigating(false);
                    clearSelection();
                    isOpeningRef.current = true;
                    inputRef.current?.focus();
                    setTimeout(() => {
                      isOpeningRef.current = false;
                    }, 200);
                  }
                }
              }}
              className="absolute opacity-0 pointer-events-none"
              style={{ position: 'absolute', top: 0, left: 0, width: 1, height: 1 }}
            />
            {/* Breadcrumb header (when drilled into category) */}
            {activeCategory && (
              <div className="flex items-center gap-1 px-3 py-2 border-b border-border text-xs text-text-2">
                <button
                  type="button"
                  onClick={handleBreadcrumbClick}
                  className="hover:text-text cursor-pointer"
                >
                  All
                </button>
                <FontAwesomeIcon
                  icon={icon({
                    name: "chevron-right",
                    family: "sharp",
                    style: "regular",
                  })}
                  className="w-2 h-2"
                />
                <span className="text-text font-medium">
                  {activeCategory}
                </span>
              </div>
            )}

            {/* Scrollable list area */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              onMouseEnter={() => {
                setIsListHovered(true);
                setIsKeyboardNavigating(false); // Switch from keyboard to mouse mode
              }}
              onMouseLeave={() => {
                setIsListHovered(false);
                clearSelection();
              }}
              data-list-hovered={isListHovered}
              data-keyboard-nav={isKeyboardNavigating}
              className="max-h-[60vh] overflow-auto p-1"
            >
              <style>{`
                [cmdk-group-heading] {
                  padding-left: 1rem; /* pl-4 */
                  padding-top: 1rem; /* pt-4 */
                  padding-bottom: 1rem; /* pb-4 */
                  font-size: 0.75rem; /* text-xs */
                  font-weight: 600; /* font-semibold */
                  font-family: 'Plus Jakarta Sans', sans-serif; /* font-family */
                  line-height: 1.25rem; /* text-sm */
                  color: hsl(var(--text)); /* text */
                  text-transform: uppercase; /* uppercase */
                  border-bottom: 1px solid hsl(var(--border)); /* border */
                }
                [cmdk-item] {
                  outline: none !important;
                }
                [cmdk-item]:focus {
                  outline: none !important;
                  box-shadow: none !important;
                }
                [cmdk-item]:focus-visible {
                  outline: none !important;
                  box-shadow: none !important;
                }
                /* Only show selection when list is being hovered, keyboard navigating, or item is focused */
                [data-list-hovered="false"][data-keyboard-nav="false"] [cmdk-item][data-selected="true"]:not(:focus) {
                  background-color: transparent;
                }
              `}</style>
              <Command.List>
                {/* Error state */}
                {error && (
                  <div className="px-3 py-4 text-sm text-danger text-center">
                    {error}
                  </div>
                )}

                {/* Category items (only in All mode) */}
                {categoryItems.length > 0 && (
                  <Command.Group heading="Category">
                    {categoryItems.map((item, index) => (
                      <Command.Item
                        key={item.key}
                        value={item.key}
                        onSelect={() => handleCategoryClick(item)}
                        onKeyDown={handleCommandItemKeyDown}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer text-sm text-text hover:bg-surface-2 data-[selected=true]:bg-surface-2 focus:outline-none ${index === 0 ? 'pt-4' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon
                            icon={icon({
                              name: "layer-group",
                              family: "sharp",
                              style: "regular",
                            })}
                            className="text-text-2 w-4 h-4"
                          />
                          <span className="text-text-2">{highlightMatch(item.term_display, query)}</span>
                        </div>
                        <FontAwesomeIcon
                          icon={icon({
                            name: "chevron-right",
                            family: "sharp",
                            style: "regular",
                          })}
                          className="text-text-3 w-3 h-3"
                        />
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                {/* Term items */}
                {termItems.length > 0 && (
                  <Command.Group heading={activeCategory || "Results"}>
                    {termItems.map((item, index) => {
                      const fullTerm = item.term_display || '';
                      // Use fixed maxChars based on dropdown width (720px) minus padding and badge space
                      // Approximate: ~60 chars fits comfortably in the available space
                      const maxChars = activeCategory ? 100 : 75;
                      const truncatedTerm = smartTruncateMiddle(fullTerm, maxChars);
                      const needsTruncation = fullTerm.length > maxChars;
                      
                      return (
                        <Command.Item
                          key={item.key}
                          value={item.key}
                          onSelect={() => handleTermClick(item)}
                          onKeyDown={handleCommandItemKeyDown}
                          className={`group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer text-sm text-text-2 hover:bg-surface-2 data-[selected=true]:bg-surface-2 focus:outline-none ${index === 0 ? 'pt-4' : ''}`}
                          aria-label={fullTerm}
                        >
                          {/* Term label container with truncation and overlay */}
                          {/* 
                            Single-line constraint: We avoid multi-line wrapping to preserve 
                            scan rhythm and visual alignment. The overlay reveals full text 
                            on hover/focus without changing row height (no layout shift).
                          */}
                          <div className="relative min-w-0 flex-1">
                            {/* Truncated visible text - single line, no wrapping */}
                            <span 
                              className="block truncate overflow-hidden text-ellipsis whitespace-nowrap"
                            >
                              {highlightMatch(truncatedTerm, query)}
                            </span>
                            
                            {/* Full term overlay - revealed on hover/focus */}
                            {/* 
                              Overlay appears on hover (mouse) or when item is active/selected 
                              (keyboard navigation via cmdk). Uses pointer-events-none to avoid 
                              blocking row interactions. Positioned absolutely to prevent layout shift.
                            */}
                            {needsTruncation && (
                              <div className="absolute left-0 top-0 z-10 bg-card border border-card-bd shadow-float rounded-md px-2 py-1 text-sm text-text-2 whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 group-data-[selected=true]:opacity-100 transition-opacity duration-150 max-w-[calc(100vw-48px)]">
                                {highlightMatch(fullTerm, query)}
                              </div>
                            )}
                          </div>
                          
                          {/* Show category badge in All mode */}
                          {!activeCategory && (
                            <span className="text-xs text-text-3 bg-surface-2 px-2 py-0.5 rounded whitespace-nowrap ml-2 flex-shrink-0">
                              {item.category_display}
                            </span>
                          )}
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                )}

                {/* Loading state */}
                {loading && (
                  <div className="px-3 py-4 text-sm text-text-2 text-center">
                    Loading...
                  </div>
                )}

                {/* Empty / no-results state */}
                {!loading && !error && (!items || items.length === 0) && (
                  mode === "ask" ? (
                    query.trim() ? (
                      <div className="flex items-center gap-2 px-3 py-3 text-sm text-text-2">
                        <FontAwesomeIcon
                          icon={icon({ name: "sparkles", family: "sharp", style: "solid" })}
                          className="text-primary text-[13px]"
                        />
                        <span>Press <span className="font-medium text-text">Enter</span> to ask this question</span>
                      </div>
                    ) : (
                      <div className="p-2">
                        <div className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-3">
                          {recentQuestions.length > 0 ? "Recent" : "Try asking"}
                        </div>
                        {(recentQuestions.length > 0
                          ? recentQuestions
                          : [
                              "Why did AI spend rise last month?",
                              "Which budgets are at risk?",
                              "How much cost is unallocated?",
                            ]
                        ).map((q) => (
                          <button
                            key={q}
                            type="button"
                            onClick={() => { setOpen(false); setQuery(""); onAsk(q, askMode); }}
                            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left text-sm text-text-2 hover:bg-surface-2 hover:text-text transition-colors"
                          >
                            <FontAwesomeIcon
                              icon={icon({ name: "sparkles", family: "sharp", style: "solid" })}
                              className="text-primary text-[12px] shrink-0"
                            />
                            <span>{q}</span>
                          </button>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="px-3 py-2 text-sm text-text-2 text-center">
                      <div>No matching results</div>
                      <div className="text-sm text-text-3">Press Enter to ask this question</div>
                    </div>
                  )
                )}
              </Command.List>
            </div>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

GlobalSearchBox.propTypes = {
  onSelectTerm: PropTypes.func.isRequired,
  onAsk: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  pageSize: PropTypes.number,
  token: PropTypes.string.isRequired,
  filters: PropTypes.array,
  excludeCategories: PropTypes.array,
  isEditMode: PropTypes.bool,
  mode: PropTypes.oneOf(["ask", "search"]),
};

export default GlobalSearchBox;

